import 'vuetify/styles';
import './MfeTestpageApp.css';
import {
  computed,
  createApp, defineComponent, ref,
  useTemplateRef,
  watch,
} from 'vue';
import { createVuetify } from 'vuetify';
import {
  VApp, VBtn, VCard, VCardText, VChip, VCol, VContainer, VDivider, VForm, VLayout, VNavigationDrawer, VRow, VSheet,
  VSkeletonLoader,
  VTextField,
} from 'vuetify/components';
import { createTestBearerToken } from '@src/testing/createTestBearerToken.js';
import { apiConsentUrl, api4pUrl } from '@src/testpage/clients/ApiConsentClient.js';
import { getDisplayConfig } from '@src/testpage/components/helpers/displayConfig.js';
import { MfeClientSelect } from '@src/testpage/components/MfeClientSelect.js';
import { MfeDrawer, type LogEntry } from '@src/testpage/components/MfeDrawer.js';
import { MfeLifecycleSelect } from '@src/testpage/components/MfeLifecycleSelect.js';
import { MfeServiceProviderSelect } from '@src/testpage/components/MfeServiceProviderSelect.js';
import html from './MfeTestpageApp.html';

interface MfeTestpageCreateAppOptions {
  port: number;
  tag: string;
  apiEndpoint: 'api-consent' | 'api-4p';
  isDev?: boolean;
  showClientSelect?: boolean;
}

interface WebHistory {
  spId: string;
  lcId: string;
  lifecycle: string;
  client?: string;
}

interface IframeMessage {
  type: string;
  height?: number;
  mfeConfig?: Record<string, string>;
  detail: Record<string, string>;
}

interface MfePopStateEvent extends PopStateEvent {
  state: WebHistory;
}

interface MfeEvent {
  message: string;
  level: string;
  name: string;
  data?: object;
  raw?: object;
}

interface IframeForMfe extends HTMLIFrameElement {
  save(): Promise<void>;
}

//
// Converts the current config to a query ?= param
//
const configToQueryString = ({
  spId, lcId, lifecycle, client,
}: WebHistory) => {
  const urlQueryParams = new URLSearchParams();
  if (spId) urlQueryParams.set('spId', spId);
  if (lcId) urlQueryParams.set('lcId', lcId);
  if (lifecycle) urlQueryParams.set('lifecycle', lifecycle);
  if (client) urlQueryParams.set('client', client);
  return `?${urlQueryParams.toString()}`;
};

// Register vuetify components manually to decrease bundle size
// Get a list using: rg -I --trim "<(v-[a-z-]+)( |>|$)" src/testpage -or '$1' | sort -u | gsed -E 's/(^|-)([a-z])/\U\2/g' | gsed 's/$/,/g'
const vuetify = createVuetify({
  components: {
    VApp,
    VBtn,
    VCard,
    VCardText,
    VChip,
    VCol,
    VContainer,
    VDivider,
    VForm,
    VLayout,
    VNavigationDrawer,
    VRow,
    VSheet,
    VSkeletonLoader,
    VTextField,
  },
  directives: {},
});

const COMPONENTS = {
  MfeDrawer,
  MfeServiceProviderSelect,
  MfeLifecycleSelect,
  MfeClientSelect,
};

//
// Adds an entry to the web history
//
const addToWebHistory = ({
  spId, lcId, lifecycle, client,
}: WebHistory) => {
  window.history.pushState({
    spId,
    lcId,
    lifecycle,
    client,
  }, '', configToQueryString({
    spId, lcId, lifecycle, client,
  }));
};

//
// Creates a microfrontend testpage and returns an intialized vue app that can bei mounted
//
export const createMfeTestpageApp = (createAppOptions: MfeTestpageCreateAppOptions) => {
  const {
    port, tag, isDev = false, showClientSelect = false, apiEndpoint,
  } = createAppOptions;

  // Add esbuild live-reload handler
  if (isDev) {
    const es = new EventSource(`http://localhost:${port + 1}/live-reload`);
    es.addEventListener('change', () => {
      window.location.reload();
    });
  }

  const MfeTestpageApp = defineComponent({
    name: 'MfeTestpageApp',
    template: html,
    setup() {
      // Definitions
      const client = ref<string>();
      const lcId = ref<string>();
      const lifecycle = ref<string>();
      const spId = ref<string>();

      const apiUrl = apiEndpoint === 'api-4p' ? api4pUrl : apiConsentUrl;
      const isMfeRendered = ref<boolean>(false);
      const logs = ref<LogEntry[]>([]);
      const logsEventsUnseenTimestamp = ref<number>(0);
      const mainScreenMessage = ref<string | undefined>('The micro frontend will render here');
      const mfeHasBeenRenderedOnce = ref<boolean>(false);
      const microfrontendInstanceHost = useTemplateRef<VSheet>('microfrontend-instance-host');
      const showClientSelectField = ref<boolean>(showClientSelect);
      const showDrawer = ref<boolean>(false);

      //
      // Logs a line/entry/event
      //
      function log(options: Omit<LogEntry, 'timestamp'>) {
        logs.value.push({
          source: 'Testpage',
          ...options,
          timestamp: Date.now(),
        });
      }

      //
      // Execute .save() on the microfrontend
      //
      async function triggerSaveOnMicrofrontend() {
        const iframe = document.querySelector('iframe');
        if (iframe) {
          try {
            await iframe.contentDocument!.querySelector<IframeForMfe>(tag)!.save();
          } catch {
            log({ level: 'error', message: 'Could not save consents. Please have a look at the DevTools.' });
          }
        }
      }

      //
      // Copy the url to the current state (central-app + MFE params) to the clipboard
      //
      async function copyUrlToClipboard() {
        let url = window.location.href;

        // CONTEXT: central-app
        // pathname: "/tef-coms/v2.24.0/easy.html"
        if (window.location.ancestorOrigins.length > 0) {
          const version = window.location.pathname.split('/')[2];
          const base = window.location.origin.replace('micro-frontend-proxy', 'central-app');
          url = `${base}/microfrontends${window.location.search}&name=${tag}&version=${version}`;
        }

        await navigator.clipboard.writeText(url);
      }

      function iframeEventHandler(event: Event) {
        const e = event as CustomEvent<MfeEvent>;

        // { "name": "consents/LOAD", "level": "info", "message": "loading consents successful"
        log({
          level: e.detail.level,
          message: e.detail.message,
          name: e.detail.name,
          source: tag,
          data: e.detail.data,
          raw: e.detail,
        });

        // On "load/READY" remove all loading indicators
        if (e.detail.name === 'load/READY') {
          mfeHasBeenRenderedOnce.value = true;
          isMfeRendered.value = true;
        }
      }

      //
      // Spawns a microfrontend
      //
      async function createMicrofrontend(options?: WebHistory) {
        if (options?.spId) spId.value = options.spId;
        if (options?.lcId) lcId.value = options.lcId;
        if (options?.lifecycle) lifecycle.value = options.lifecycle;
        if (showClientSelect && options?.client) {
          client.value = options.client;
        }

        // delete the old state/mfe
        isMfeRendered.value = false;
        window.sessionStorage.clear();
        window.document.querySelectorAll('iframe').forEach((iframeTag) => iframeTag.remove());
        logs.value = [];

        // If a client is required, don't render anything until it is provided
        if (showClientSelect && !client.value) {
          return;
        }

        // Create microfrontend tag
        const mfeTag = window.document.createElement(tag);

        const accessToken = await createTestBearerToken({
          username: ['491797250005', 'foo@bar.de'],
          spID: spId.value,
          domainModel: [
            {
              customerAccountId: lcId.value,
              role: 'Owner',
            },
          ],
          scope: ['openid'],
        });

        const displayConfig = getDisplayConfig({ spId: spId.value!, client: client.value });
        const additionalResources = { head: '', body: '' };

        if (displayConfig.system === 'tefOne') {
          additionalResources.head = `<link rel="stylesheet" href="${displayConfig.uri}/variables.css" /><link rel="stylesheet" href="${displayConfig.uri}/components.css" />`;
          additionalResources.body = `<script src="${displayConfig.uri}/../bundle.js"></script>`;
        }

        // Pre-load the CSS so the fonts contained therein are loaded.
        // Otherwise the fonts are not available in the shadowDOM:
        // See bug: https://issues.chromium.org/issues/41085401
        if (displayConfig.system === 'occl') {
          additionalResources.head = `<link rel="stylesheet" href="${displayConfig.uri}/v3/pre-sets/coms/bundle.css" />`;
        }

        mfeTag.setAttribute('lifecycle', lifecycle.value!);

        // tef-coms and tef-com4cs
        if (apiEndpoint === 'api-consent') {
          mfeTag.setAttribute('spid', spId.value!);
          mfeTag.setAttribute('lcid', lcId.value!);
          mfeTag.setAttribute('accesstoken', accessToken);
          mfeTag.setAttribute('gatewaytoken', accessToken);
          mfeTag.setAttribute('apiuri', apiUrl);

          if (displayConfig.system === 'occl') {
            mfeTag.setAttribute('occluri', displayConfig.uri);
          }

          if (displayConfig.mode) {
            mfeTag.setAttribute('mode', displayConfig.mode);
          }
        }

        // For tef-coms4p and tef-com4p4cs
        if (apiEndpoint === 'api-4p') {
          mfeTag.setAttribute('sp-id', spId.value!);
          mfeTag.setAttribute('lc-id', lcId.value!);
          mfeTag.setAttribute('api-url', apiUrl);
          mfeTag.setAttribute('theme', displayConfig.system);
          mfeTag.setAttribute('theme-base-url', displayConfig.uri);
          mfeTag.setAttribute('access-token', accessToken);
        }

        // Create container <iframe> with all neccessary HTML/JS/CSS
        const iframe = window.document.createElement('iframe');
        const iframeSrcdoc = `
        <!doctype html>
        <html>
          <head>
            <meta charset="utf-8" />
            ${additionalResources.head}
          </head>
          <body style="overflow: hidden">
          ${additionalResources.body}
            <script type="module" data-mfe-tag="${tag}" src="${tag}.js"></script>
            ${mfeTag.outerHTML}
            <script type="module">
              // Some MFEs are too fast and send events when the "load" of the iframe
              // is not done. We need to buffer those events and send them later.
              let iframeLoaded = false;
              const eventBuffer = [];
              let previousHeight = 0;

              // Send the height of the body to the parent iframe when it changes
              // to some degree
              function sendHeight() {
                const rect = document.body.getBoundingClientRect();
                const height = Math.ceil(rect.height + Math.abs(rect.top) + 1);
                if (height > previousHeight + 50) {
                  parent.postMessage({ type: 'resize', height: height }, '*');
                  previousHeight = height;
                }
              }
              new ResizeObserver(sendHeight).observe(document.body);

              const tag = document.querySelector("[data-mfe-tag]").dataset.mfeTag;
              document.addEventListener(tag, (e) => {
                if(!iframeLoaded) {
                  // "load" event not triggered? Buffer events.
                  eventBuffer.push(e);
                } else {
                  parent.postMessage({ type: 'mfe', detail: e.detail }, '*');
                }
              });

              // "load" event on the iframe triggered?
              // Send events as postMessage.
              window.addEventListener("load", () => {
                iframeLoaded = true;
                eventBuffer.forEach((e) => {
                  parent.postMessage({ type: 'mfe', detail: e.detail }, '*');
                });
              });
            </script>
          </body>
        </html>`;
        iframe.srcdoc = iframeSrcdoc;
        iframe.style.height = '0';

        (microfrontendInstanceHost.value!.$el as HTMLDivElement).appendChild(iframe);

        // Update web history
        addToWebHistory({
          spId: spId.value!,
          lcId: lcId.value!,
          lifecycle: lifecycle.value!,
          client: client.value!,
        });

        // Check if we are called via central-app. If yes send message about config state across the iframe.
        if (window !== window.parent) {
          const version = window.location.pathname.match(/\/(v[0-9.]+)/); // eslint-disable-line sonarjs/prefer-regexp-exec
          const params: Record<string, string> = {
            // Mandatory Params:
            spId: spId.value!,
            lcId: lcId.value!,
            lifecycle: lifecycle.value!,
          };

          if (client.value) {
            params.client = client.value;
          }

          // eslint-disable-next-line sonarjs/post-message
          window.parent.postMessage({
            mfeConfig: {
              name: tag,
              version: version ? version[1] : 'latest',
              params,
            },
          }, '*');
        }
      }

      //
      // computed properties
      //
      const isCreateButtonActive = computed(() => spId.value && lcId.value && lifecycle.value && (showClientSelectField.value ? client.value : true));
      const unreadNotificationCount = computed(() => logs.value.filter((item) => item.timestamp > logsEventsUnseenTimestamp.value).length);
      const advancedModeUrl = computed(() => `test.html${configToQueryString({ spId: spId.value!, lcId: lcId.value!, lifecycle: lifecycle.value! })}`);
      const pageHeadline = computed(() => {
        const versionMatch = window.location.pathname.match(/\/(v[0-9.]+)/); // eslint-disable-line 
        const version = versionMatch ? versionMatch[1] : 'latest';
        return `${tag} (Version: ${version})`;
      });

      //
      // web history listener (aka. back button etc)
      //
      window.addEventListener('popstate', (event: MfePopStateEvent) => {
        if (event.state) {
          createMicrofrontend(event.state); // eslint-disable-line @typescript-eslint/no-floating-promises
        }
      });

      //
      // Listen for messages from the iframe (send by central-app)
      //
      window.addEventListener('message', (event: MessageEvent<IframeMessage>) => { // eslint-disable-line sonarjs/post-message
        const iframe = document.querySelector('iframe');

        if (iframe) {
          // inner resize event
          if (event.data.type === 'resize' && event.data.height && event.data.height > 0) {
            iframe.style.height = `${event.data.height}px`;
            mainScreenMessage.value = undefined;
          }

          // The MFE rendered as 0 pixel height or has no consents
          const isResizeEventToZeroPixel = event.data.type === 'resize' && event.data.height === 0;
          const isEmptyEvent = event.data.type === 'mfe' && event.data.detail.name === 'consents/EMPTY';
          if (isResizeEventToZeroPixel || isEmptyEvent) {
            mainScreenMessage.value = 'The micro frontend did not render any HTML. Please see notifications!';
            isMfeRendered.value = true;
            iframe.style.height = '0';
          }

          // Got an event originally send to the tag by the MFE via postMessage fro mthe iframe
          if (event.data.type === 'mfe') {
            const convertedToEvent = new CustomEvent(tag, {
              bubbles: true,
              composed: true,
              detail: {
                ...event.data.detail,
              },
            });
            iframeEventHandler(convertedToEvent);
          }
        }
      });

      //
      // restore state from reload
      //
      const urlQueryParams = new URLSearchParams(window.location.search);
      if (urlQueryParams.size > 0) {
        const initParams = Object.fromEntries(urlQueryParams.entries()) as unknown as WebHistory;
        createMicrofrontend(initParams); // eslint-disable-line @typescript-eslint/no-floating-promises
      }

      // If the drawer is opened, reset the "unseen" notification counter
      watch(
        () => showDrawer.value,
        (currentValue: boolean) => {
          if (currentValue) {
            logsEventsUnseenTimestamp.value = Date.now();
          }
        },
      );

      // If the configuration changes, reset notification logs
      watch(
        [spId, lcId, lifecycle, client],
        () => { logs.value = []; },
      );

      // Return everything that is needen in the template
      return {
        advancedModeUrl,
        client,
        copyUrlToClipboard,
        createMicrofrontend,
        isCreateButtonActive,
        isMfeRendered,
        lcId,
        lifecycle,
        logs,
        logsEventsUnseenTimestamp,
        mainScreenMessage,
        mfeHasBeenRenderedOnce,
        pageHeadline,
        showClientSelectField,
        showDrawer,
        spId,
        tag,
        triggerSaveOnMicrofrontend,
        unreadNotificationCount,
      };
    },
  });

  const app = createApp(MfeTestpageApp).use(vuetify);

  // Our own components must be registered globally so that every comp can access them.
  Object.entries(COMPONENTS).forEach(([name, componentClass]) => {
    app.component(name, componentClass);
  });

  return app;
};
